import { Fragment, useState } from 'react';
import { Link } from 'react-router';
import { motion } from 'motion/react';
import { gameLabel } from '@redpandev/shared';
import type { MatchHistoryItemDto } from '@redpandev/shared';
import { Button, Card, Mascot, Spinner, fadeInUp, staggerContainer } from '@redpandev/ui';
import { formatRelativeDate } from './relativeTime';

type ResultLook = { label: string; glyph: string; dot: string; text: string };

/** Pastille = couleur + glyphe (jamais la couleur seule — daltonisme). */
const RESULTS: Record<string, ResultLook> = {
  win: {
    label: 'Victoire',
    glyph: '✓',
    dot: 'border-bamboo-500 bg-bamboo-500/15 text-bamboo-400',
    text: 'text-bamboo-400',
  },
  loss: {
    label: 'Défaite',
    glyph: '✗',
    dot: 'border-danger-500 bg-danger-500/15 text-danger-500',
    text: 'text-danger-500',
  },
  draw: {
    label: 'Égalité',
    glyph: '=',
    dot: 'border-ink-600 bg-ink-100/10 text-ink-100',
    text: 'text-ink-100',
  },
  abandon: {
    label: 'Abandon',
    glyph: '⚑',
    dot: 'border-sun-500 bg-sun-500/15 text-sun-400',
    text: 'text-sun-400',
  },
};

const FALLBACK: ResultLook = {
  label: 'Partie',
  glyph: '·',
  dot: 'border-ink-600 bg-ink-100/10 text-ink-400',
  text: 'text-ink-400',
};

/**
 * Timeline verticale de l'historique (données publiques renvoyées par l'API —
 * résultats de parties terminées uniquement). Stagger d'apparition.
 * `limit` : tronque avec « Voir plus » — seulement si ≥ 2 cachées (spec 08 §3).
 * « Voir plus » est hybride : il RÉVÈLE d'abord le déjà-chargé, puis quand
 * tout est affiché et que `hasMore`, il va chercher la page suivante
 * (`onLoadMore`, pagination par curseur côté API).
 */
export function HistoryTimeline({
  items,
  error = false,
  limit,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}: {
  items: MatchHistoryItemDto[] | null;
  error?: boolean;
  limit?: number;
  /** Il reste des pages côté serveur (nextCursor présent). */
  hasMore?: boolean;
  loadingMore?: boolean;
  /** Charge la page suivante et renvoie le nombre de parties ajoutées. */
  onLoadMore?: () => Promise<number>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [announce, setAnnounce] = useState('');

  if (error) {
    return (
      <p role="alert" className="text-sm text-danger-500">
        Impossible de charger l'historique. Réessaie plus tard.
      </p>
    );
  }

  if (items === null) {
    return <Spinner label="Chargement de l'historique…" className="mx-auto" />;
  }

  if (items.length === 0) {
    return (
      <Card className="flex flex-col items-center gap-3 p-6 text-center">
        <Mascot mood="shy" size={72} animated={false} />
        <p className="text-sm text-ink-400">
          Rien à raconter pour l'instant… Chaque légende commence par une première partie !
        </p>
      </Card>
    );
  }

  const hiddenCount = limit !== undefined && !expanded ? items.length - limit : 0;
  const truncated = hiddenCount >= 2;
  const visible = truncated ? items.slice(0, limit) : items;

  async function onSeeMore() {
    if (truncated) {
      // 1er niveau : révéler le déjà-chargé (aucun appel réseau).
      setExpanded(true);
      setAnnounce(`${hiddenCount} parties supplémentaires affichées`);
      return;
    }
    // 2e niveau : aller chercher la page suivante côté serveur.
    if (!onLoadMore) return;
    const added = await onLoadMore();
    setExpanded(true);
    setAnnounce(
      added > 0
        ? `${added} parties supplémentaires affichées`
        : 'Pas de partie plus ancienne à afficher',
    );
  }

  return (
    <div>
      <motion.ol
        variants={staggerContainer(0.05)}
        initial="hidden"
        animate="visible"
        className="ml-4 space-y-4 border-l-2 border-ink-600/60 pl-8"
      >
        {visible.map((item) => (
          <HistoryItem key={item.matchId} item={item} />
        ))}
      </motion.ol>
      {(truncated || (hasMore && onLoadMore)) && (
        <Button
          variant="ghost"
          size="sm"
          className="ml-12 mt-3"
          loading={loadingMore}
          aria-label="Afficher les parties plus anciennes"
          onClick={() => void onSeeMore()}
        >
          {truncated ? `Voir plus (${hiddenCount}${hasMore ? '+' : ''})` : 'Voir plus'}
        </Button>
      )}
      <p role="status" aria-live="polite" className="sr-only">
        {announce}
      </p>
    </div>
  );
}

function HistoryItem({ item }: { item: MatchHistoryItemDto }) {
  const look = (item.result && RESULTS[item.result]) || FALLBACK;
  const when = item.finishedAt ? formatRelativeDate(item.finishedAt) : '';

  return (
    <motion.li variants={fadeInUp} className="relative">
      <span
        aria-hidden="true"
        className={`absolute -left-[3.05rem] top-3 flex h-8 w-8 items-center justify-center rounded-full border-2 font-display font-bold ${look.dot}`}
      >
        {look.glyph}
      </span>

      <Card className="p-4">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className={`font-display font-bold ${look.text}`}>{look.label}</span>
          <span className="text-xs font-semibold uppercase tracking-wide text-ink-400">
            {gameLabel(item.game)}
          </span>
          {item.finishedAt && (
            <time dateTime={item.finishedAt} className="ml-auto text-xs text-ink-400">
              {when}
            </time>
          )}
        </div>

        <p className="mt-1 text-sm text-ink-100/80">
          {item.game === 'motus-daily' && item.opponents.length === 0 ? (
            // Jeu quotidien SOLO (ADR 0011) : pas d'adversaire, ce n'est pas une anomalie.
            <span className="italic text-ink-400">en solo</span>
          ) : (
            <>
              contre{' '}
              {item.opponents.length === 0 ? (
                <span className="italic text-ink-400">adversaire mystère</span>
              ) : (
                item.opponents.map((o, i) => (
                  <Fragment key={o.displayName}>
                    {i > 0 && ', '}
                    <Link
                      to={`/u/${encodeURIComponent(o.displayName)}`}
                      className="font-semibold text-sky-400 hover:underline"
                    >
                      {o.displayName}
                    </Link>
                  </Fragment>
                ))
              )}
            </>
          )}
        </p>
      </Card>
    </motion.li>
  );
}
